import React, { useMemo } from 'react';
import MetricCard from '../components/MetricCard';
import { usePortal } from '../context/PortalContext';
import { formatCompactCurrency, formatCurrency, formatDate } from '../utils/formatters';

const ClubsPage: React.FC = () => {
  const { ownedClubs, selectedClub, selectedClubId, setSelectedClubId } = usePortal();

  const totals = useMemo(() => {
    return ownedClubs.reduce(
      (accumulator, club) => {
        accumulator.members += Number(club.total_members || 0);
        accumulator.tables += Number(club.total_tables || 0);
        accumulator.rake += Number(club.total_rake_earned || 0);
        return accumulator;
      },
      {
        members: 0,
        tables: 0,
        rake: 0,
      }
    );
  }, [ownedClubs]);

  return (
    <div className="portal-page-grid portal-page-stack">
      <section className="portal-section-card portal-section-card--hero portal-section-card--dashboard-hero">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Ownership real</p>
            <h3>Clubs do usuario autenticado</h3>
          </div>
          <div className="portal-section-card__header-actions">
            <span className="portal-chip">{ownedClubs.length} club(s)</span>
            {selectedClub ? <span className="portal-chip">Ativo: {selectedClub.code}</span> : null}
          </div>
        </div>

        <p className="portal-muted">
          Aqui ficam os clubs que o owner realmente controla no backend. A selecao abaixo define o contexto do
          portal inteiro.
        </p>

        <div className="portal-dashboard-hero-stats">
          <article className="portal-dashboard-hero-stats__item">
            <span>Clubs</span>
            <strong>{ownedClubs.length}</strong>
          </article>
          <article className="portal-dashboard-hero-stats__item">
            <span>Membros somados</span>
            <strong>{totals.members}</strong>
          </article>
          <article className="portal-dashboard-hero-stats__item">
            <span>Mesas somadas</span>
            <strong>{totals.tables}</strong>
          </article>
          <article className="portal-dashboard-hero-stats__item">
            <span>Rake consolidado</span>
            <strong>{formatCompactCurrency(totals.rake)}</strong>
          </article>
        </div>
      </section>

      <section className="portal-metrics-grid portal-dashboard-metrics-grid">
        <MetricCard
          label="Club ativo"
          value={selectedClub?.name || 'Nenhum'}
          helper={selectedClub ? `Codigo ${selectedClub.code}` : 'Selecione um club abaixo'}
          variant="dashboard"
          theme="rake"
          badge="Contexto"
        />
        <MetricCard
          label="Rake total"
          value={formatCurrency(totals.rake)}
          helper="Somatorio dos clubs listados"
          variant="dashboard"
          theme="deposit"
          badge="Rake"
        />
        <MetricCard
          label="Membros"
          value={String(totals.members)}
          helper="Membros somados entre os clubs"
          variant="dashboard"
          theme="balance"
          badge="Equipe"
        />
        <MetricCard
          label="Mesas"
          value={String(totals.tables)}
          helper="Mesas somadas entre os clubs"
          variant="dashboard"
          theme="warning"
          badge="Mesas"
        />
      </section>

      <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-balance">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Ownership real</p>
            <h3>Lista de clubs</h3>
          </div>
        </div>

        <div className="portal-club-grid portal-club-grid--dashboard">
          {ownedClubs.length === 0 ? (
            <p className="portal-muted">Este usuario nao possui clubs cadastrados no backend.</p>
          ) : (
            ownedClubs.map((club) => {
              const isActive = club.id === selectedClubId;
              return (
                <article
                  key={club.id}
                  className={`portal-club-card portal-club-card--dashboard${isActive ? ' portal-club-card--active' : ''}`}
                >
                  <div className="portal-club-card__hero">
                    <div>
                      <p className="portal-section-card__eyebrow">{club.code}</p>
                      <h4>{club.name}</h4>
                    </div>
                    <span className={`portal-status-pill ${isActive ? 'portal-status-pill--positive' : 'portal-status-pill--neutral'}`}>
                      {isActive ? 'Selecionado' : 'Disponivel'}
                    </span>
                  </div>

                  <p className="portal-muted">{club.description || 'Sem descricao cadastrada.'}</p>

                  <dl className="portal-inline-stats portal-inline-stats--dashboard">
                    <div>
                      <dt>Membros</dt>
                      <dd>{club.total_members}</dd>
                    </div>
                    <div>
                      <dt>Mesas</dt>
                      <dd>{club.total_tables}</dd>
                    </div>
                    <div>
                      <dt>Rake</dt>
                      <dd>{formatCurrency(club.total_rake_earned || 0)}</dd>
                    </div>
                  </dl>

                  <footer className="portal-card-actions">
                    <span className="portal-muted">Criado em {formatDate(club.created_at)}</span>
                    <button
                      type="button"
                      className={isActive ? 'portal-primary-button' : 'portal-secondary-button'}
                      onClick={() => setSelectedClubId(club.id)}
                    >
                      {isActive ? 'Contexto ativo' : 'Usar no portal'}
                    </button>
                  </footer>
                </article>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
};

export default ClubsPage;
